import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Space, message, Input, Select, Modal, Image, Tag, Drawer, Dropdown, Checkbox, Switch, Card, Spin, Tooltip } from 'antd';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchProducts, fetchProductVariants, fetchProductImages, deleteProduct, type ProductDto, type ProductVariantDto, type ProductImageDto, type AttributeFilterParam } from '../services/productService';
import { fetchCategoryAttributes, AttributeDataType, type CategoryAttributeDto } from '../services/categoryAttributeService';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PictureOutlined, MenuUnfoldOutlined, MenuFoldOutlined, CarOutlined, UnorderedListOutlined, FileExcelOutlined } from '@ant-design/icons';
import { api, FILE_BASE_URL } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { useCanDelete } from '../hooks/useCanDelete';
import { fetchProductCompatibilities, fetchBrands, fetchModels, type ProductBrandCompatibilityDto, type BrandDto, type ModelDto } from '../services/vehicleService';
import { fetchProductBrands, type ProductBrandDto } from '../services/productBrandService';
import { downloadCsvAsExcel } from '../utils/excelExport';

type Product = ProductDto;

interface Category {
  id: number;
  categoryName: string;
  parentId?: number | null;
}

const COLUMN_LABELS: Record<string, string> = {
  productCodeBrand: 'Ürün Kodu · Marka',
  productName: 'Ürün Adı',
  image: 'Görseller',
  totalStock: 'Toplam Stok',
  barcode: 'Barkod',
  categoryName: 'Kategori',
  vehicleCompatibility: 'Araç Uyumluluğu',
  baseUnitName: 'Birim',
  purchasePrice: 'Alış Fiyatı (KDV dahil)',
  retailPrice: 'Perakende Fiyatı (KDV dahil)',
  wholesalePrice: 'Toptan Fiyatı (KDV dahil)',
  isActive: 'Durum'};

const ALWAYS_VISIBLE_KEYS = ['expand', 'actions'];
const DEFAULT_VISIBLE_KEYS = ['expand', 'productCodeBrand', 'productName', 'image', 'totalStock', 'barcode', 'categoryName', 'vehicleCompatibility', 'baseUnitName', 'purchasePrice', 'retailPrice', 'wholesalePrice', 'isActive', 'actions'];

const PRODUCTS_COLUMN_STORAGE_KEY = 'otoerp.products.visibleColumns';

const ALL_KNOWN_COLUMN_KEYS = [...ALWAYS_VISIBLE_KEYS, ...Object.keys(COLUMN_LABELS)];

function loadProductListVisibleColumnsFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(PRODUCTS_COLUMN_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_KEYS;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_KEYS;
    const valid = new Set(ALL_KNOWN_COLUMN_KEYS);
    const merged = parsed.filter((k): k is string => typeof k === 'string' && valid.has(k));
    const withFixed = Array.from(new Set([...ALWAYS_VISIBLE_KEYS, ...merged]));
    const toggleable = withFixed.filter((k) => !ALWAYS_VISIBLE_KEYS.includes(k));
    if (toggleable.length === 0) return DEFAULT_VISIBLE_KEYS;
    return withFixed;
  } catch {
    return DEFAULT_VISIBLE_KEYS;
  }
}

interface ProductMovementPreviewItem {
  id: number;
  movementDate: string;
  movementLabel: string;
  quantity: number;
  documentNo?: string | null;
  serviceOrderNo?: string | null;
  serviceCustomerName?: string | null;
  unitPrice?: number | null;
  totalPrice?: number | null;
}

interface ProductMovementPreviewResponse {
  productId: number;
  productCode: string;
  productName: string;
  summary: {
    lastPurchasePrice?: number | null;
    lastSalePrice?: number | null;
    systemSalePrice?: number | null;
    averagePurchasePrice?: number | null;
  };
  items: ProductMovementPreviewItem[];
}

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDeleteGlobal = useCanDelete();
  const roleCodes = user?.roleCodes ?? [];
  const canDeleteProduct = (roleCodes.includes('ADMIN') || roleCodes.includes('YONETICI')) && canDeleteGlobal;
  const canSeePurchasePrice = roleCodes.includes('ADMIN');
  const [products, setProducts] = useState<Product[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [variantsByProduct, setVariantsByProduct] = useState<Record<number, ProductVariantDto[]>>({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedProductBrand, setSelectedProductBrand] = useState<number | undefined>();
  const [selectedBrand, setSelectedBrand] = useState<number | undefined>();
  const [selectedModel, setSelectedModel] = useState<number | undefined>();
  const [yearFrom, setYearFrom] = useState<number | undefined>();
  const [yearTo, setYearTo] = useState<number | undefined>();
  const [inStockOnly, setInStockOnly] = useState<boolean>(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productBrands, setProductBrands] = useState<ProductBrandDto[]>([]);
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0});
  const [imageGalleryVisible, setImageGalleryVisible] = useState(false);
  const [selectedProductImages, setSelectedProductImages] = useState<ProductImageDto[]>([]);
  const [productsWithImages, setProductsWithImages] = useState<Record<number, boolean>>({});
  const [productCompatibilities, setProductCompatibilities] = useState<Record<number, ProductBrandCompatibilityDto[]>>({});
  const [categoryAttributeFilters, setCategoryAttributeFilters] = useState<CategoryAttributeDto[]>([]);
  const [attributeFilterValues, setAttributeFilterValues] = useState<Record<number, { min?: number; max?: number; value?: string; dateMin?: string; dateMax?: string }>>({});
  const [compatibilityDrawerOpen, setCompatibilityDrawerOpen] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(loadProductListVisibleColumnsFromStorage);
  const { baseCurrencySymbol } = useCurrency();

  useEffect(() => {
    try {
      localStorage.setItem(PRODUCTS_COLUMN_STORAGE_KEY, JSON.stringify(visibleColumnKeys));
    } catch {
      /* ignore quota / private mode */
    }
  }, [visibleColumnKeys]);

  const [movementsModalOpen, setMovementsModalOpen] = useState(false);
  const [movementsTargetProductId, setMovementsTargetProductId] = useState<number | null>(null);
  const [movementsPreviewLoading, setMovementsPreviewLoading] = useState(false);
  const [movementsPreview, setMovementsPreview] = useState<ProductMovementPreviewResponse | null>(null);
  const [exportingExcel, setExportingExcel] = useState(false);

  const formatMovementMoney = (v?: number | null) =>
    v == null ? '-' : `${baseCurrencySymbol}${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const closeMovementsModal = () => {
    setMovementsModalOpen(false);
    setMovementsPreview(null);
    setMovementsTargetProductId(null);
  };

  const openMovementsPreview = async (productId: number) => {
    setMovementsTargetProductId(productId);
    setMovementsModalOpen(true);
    setMovementsPreview(null);
    setMovementsPreviewLoading(true);
    try {
      const { data } = await api.get<ProductMovementPreviewResponse>(`/products/${productId}/movements`, {
        params: { itemsLimit: 5 }});
      setMovementsPreview(data);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Hareket özeti yüklenirken hata oluştu');
      closeMovementsModal();
    } finally {
      setMovementsPreviewLoading(false);
    }
  };

  const goToFullMovementsPage = () => {
    const pid = movementsPreview?.productId ?? movementsTargetProductId;
    if (pid == null) return;
    navigate(`/products/${pid}/movements`);
    closeMovementsModal();
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get<Category[]>('/categories');
      setCategories(data);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata oluştu:', error);
    }
  };

  const loadProductBrands = async () => {
    try {
      const data = await fetchProductBrands();
      setProductBrands(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ürün markaları yüklenirken hata oluştu:', error);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch (error) {
      console.error('Markalar yüklenirken hata oluştu:', error);
    }
  };

  const loadModels = async () => {
    if (selectedBrand) {
      try {
        const data = await fetchModels(selectedBrand);
        setModels(data);
      } catch (error) {
        console.error('Modeller yüklenirken hata oluştu:', error);
      }
    } else {
      setModels([]);
      setSelectedModel(undefined);
    }
  };

  useEffect(() => {
    if (!selectedCategory) {
      setCategoryAttributeFilters([]);
      setAttributeFilterValues({});
      return;
    }
    (async () => {
      try {
        const attrs = await fetchCategoryAttributes(selectedCategory);
        setCategoryAttributeFilters(attrs.filter(a => a.isFilterable));
        setAttributeFilterValues({});
      } catch {
        setCategoryAttributeFilters([]);
      }
    })();
  }, [selectedCategory]);

  const buildAttributeFilters = (): AttributeFilterParam[] => {
    const result: AttributeFilterParam[] = [];
    for (const attr of categoryAttributeFilters) {
      const v = attributeFilterValues[attr.id];
      if (!v) continue;
      if (attr.dataType === AttributeDataType.Number && (v.min != null || v.max != null)) {
        result.push({ categoryAttributeId: attr.id, valueMin: v.min, valueMax: v.max });
      } else if (attr.dataType === AttributeDataType.Text && v.value != null && v.value.trim() !== '') {
        result.push({ categoryAttributeId: attr.id, value: v.value.trim() });
      } else if (attr.dataType === AttributeDataType.Date && (v.dateMin || v.dateMax)) {
        result.push({ categoryAttributeId: attr.id, valueDateMin: v.dateMin, valueDateMax: v.dateMax });
      }
    }
    return result;
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const attrFilters = buildAttributeFilters();
      const result = await fetchProducts(
        pagination.current,
        pagination.pageSize,
        selectedCategory,
        searchText.trim() || undefined,
        undefined,
        selectedProductBrand,
        selectedBrand,
        selectedModel,
        yearFrom,
        yearTo,
        attrFilters.length > 0 ? attrFilters : undefined,
        inStockOnly
      );
      // API cevabı: { data: ProductDto[], totalCount, page, pageSize }
      const items = Array.isArray(result.data) ? result.data : [];
      setProducts(items);
      setPagination(prev => ({
        ...prev,
        total: result.totalCount ?? 0}));
    } catch (error) {
      console.error('Ürünler yüklenirken hata:', error);
      message.error('Ürünler yüklenirken hata oluştu');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchText, selectedProductBrand, selectedBrand, selectedModel, yearFrom, yearTo, pagination.current, pagination.pageSize, attributeFilterValues, categoryAttributeFilters, inStockOnly]);

  useEffect(() => {
    loadCategories();
    loadProductBrands();
    loadBrands();
  }, []);

  useEffect(() => {
    loadModels();
  }, [selectedBrand]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadProducts();
  };

  const handleExpandAll = async () => {
    // Tüm expandable satırları al (isPriceVariant true olanlar)
    const expandableProductIds = products
      .filter(p => p.isPriceVariant)
      .map(p => p.id);
    
    if (expandableProductIds.length === 0) {
      message.info('Açılabilir varyantı olan ürün bulunmamaktadır.');
      return;
    }

    // Tüm varyantları yükle
    const variantPromises = expandableProductIds.map(async (productId) => {
      if (!variantsByProduct[productId]) {
        try {
          const rows = await fetchProductVariants(productId);
          setVariantsByProduct(prev => ({ ...prev, [productId]: rows }));
          return { productId, hasVariants: rows.length > 0 };
        } catch (error) {
          console.error(`Varyantlar yüklenirken hata (ProductId: ${productId}):`, error);
          return { productId, hasVariants: false };
        }
      }
      return { productId, hasVariants: variantsByProduct[productId].length > 0 };
    });

    const results = await Promise.all(variantPromises);
    
    // Sadece varyantı olan ürünlerin ID'lerini expandedRowKeys'e ekle
    const validProductIds = results
      .filter(r => r.hasVariants)
      .map(r => r.productId);
    
    setExpandedRowKeys(validProductIds);
    
    if (validProductIds.length === 0) {
      message.info('Açılabilir varyantı olan ürün bulunmamaktadır.');
    } else {
      message.success(`${validProductIds.length} ürünün varyantları açıldı.`);
    }
  };

  const handleCollapseAll = () => {
    setExpandedRowKeys([]);
    message.success('Tüm varyantlar kapatıldı.');
  };

  const formatPriceWithTax = (price?: number | null, taxRate?: number | null) => {
    if (price == null) return '';
    const withTax = price * (1 + (taxRate ?? 20) / 100);
    return withTax.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      const attrFilters = buildAttributeFilters();
      const exportPageSize = 500;
      let page = 1;
      let totalCount = Infinity;
      const allProducts: ProductDto[] = [];

      while (allProducts.length < totalCount) {
        const result = await fetchProducts(
          page,
          exportPageSize,
          selectedCategory,
          searchText.trim() || undefined,
          undefined,
          selectedProductBrand,
          selectedBrand,
          selectedModel,
          yearFrom,
          yearTo,
          attrFilters.length > 0 ? attrFilters : undefined,
          inStockOnly
        );
        totalCount = result.totalCount ?? 0;
        const items = Array.isArray(result.data) ? result.data : [];
        if (items.length === 0) break;
        allProducts.push(...items);
        if (allProducts.length >= totalCount) break;
        page += 1;
        if (page > 200) break;
      }

      if (allProducts.length === 0) {
        message.warning('Aktarılacak ürün bulunamadı.');
        return;
      }

      const exportColumnKeys = visibleColumnKeys.filter(
        (k) => !ALWAYS_VISIBLE_KEYS.includes(k) && k !== 'image' && k !== 'vehicleCompatibility'
      );

      const headers: string[] = [];
      for (const key of exportColumnKeys) {
        if (key === 'productCodeBrand') {
          headers.push('Ürün Kodu', 'Marka');
        } else if (key === 'purchasePrice' && !canSeePurchasePrice) {
          continue;
        } else {
          headers.push(COLUMN_LABELS[key] ?? key);
        }
      }

      const rows = allProducts.map((p) => {
        const row: Array<string | number | null | undefined> = [];
        for (const key of exportColumnKeys) {
          if (key === 'productCodeBrand') {
            row.push(p.productCode ?? '', p.productBrandName ?? '');
          } else if (key === 'productName') {
            row.push(p.productName ?? '');
          } else if (key === 'totalStock') {
            row.push(p.totalStock ?? '');
          } else if (key === 'barcode') {
            row.push(p.barcode ?? '');
          } else if (key === 'categoryName') {
            row.push(p.categoryName ?? '');
          } else if (key === 'baseUnitName') {
            row.push(p.baseUnitName ?? '');
          } else if (key === 'purchasePrice') {
            if (!canSeePurchasePrice) continue;
            row.push(formatPriceWithTax(p.purchasePrice, p.taxRate));
          } else if (key === 'retailPrice') {
            row.push(formatPriceWithTax(p.retailPrice, p.taxRate));
          } else if (key === 'wholesalePrice') {
            row.push(formatPriceWithTax(p.wholesalePrice, p.taxRate));
          } else if (key === 'isActive') {
            row.push(p.isActive ? 'Aktif' : 'Pasif');
          }
        }
        return row;
      });

      downloadCsvAsExcel('urunler', headers, rows);
      message.success(`${allProducts.length} ürün Excel'e aktarıldı.`);
    } catch (error) {
      console.error('Excel aktarımı hatası:', error);
      message.error('Excel aktarımı sırasında hata oluştu');
    } finally {
      setExportingExcel(false);
    }
  };

  const expandableProducts = useMemo(() => 
    products.filter(p => p.isPriceVariant), 
    [products]
  );

  const isAllExpanded = useMemo(() => 
    expandableProducts.length > 0 && 
    expandableProducts.every(p => {
      const variants = variantsByProduct[p.id];
      // Eğer varyantlar yüklenmemişse, expandedRowKeys'de olmamalı
      if (!variants) return false;
      // Varyant varsa ve expandedRowKeys'de ise açık demektir
      return variants.length > 0 && expandedRowKeys.includes(p.id);
    }),
    [expandableProducts, variantsByProduct, expandedRowKeys]
  );

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total});
  };

  const handleAdd = () => {
    navigate('/products/add');
  };

  const handleEdit = (product: Product) => {
    navigate(`/products/edit/${product.id}`);
  };

  const handleDelete = async (product: Product) => {
    const stock = product.totalStock ?? 0;
    if (stock > 0) {
      message.warning('Stoğu olan ürün silinemez. Önce stoğu sıfırlamanız veya stok hareketi ile düşürmeniz gerekir.');
      return;
    }
    try {
      await deleteProduct(product.id);
      message.success('Ürün silindi');
      loadProducts();
    } catch (error: any) {
      message.error(error.response?.data?.message ?? 'Ürün silinirken hata oluştu');
    }
  };

  const checkProductImages = useCallback(async (productId: number) => {
    try {
      const images = await fetchProductImages(productId);
      setProductsWithImages(prev => ({
        ...prev,
        [productId]: images.length > 0
      }));
    } catch (error) {
      // Silently fail if images can't be loaded
      setProductsWithImages(prev => ({
        ...prev,
        [productId]: false
      }));
    }
  }, []);

  const handleImageIconClick = async (product: Product) => {
    try {
      const images = await fetchProductImages(product.id);
      if (images.length > 0) {
        setSelectedProductImages(images);
        setImageGalleryVisible(true);
      } else {
        message.info('Bu ürün için görsel bulunmamaktadır.');
      }
    } catch (error) {
      message.error('Görseller yüklenirken hata oluştu');
    }
  };

  // Check images for products when they're loaded
  useEffect(() => {
    products.forEach(product => {
      if (productsWithImages[product.id] === undefined) {
        checkProductImages(product.id);
      }
    });
  }, [products, checkProductImages]);

  // Load vehicle compatibilities for products
  const loadProductCompatibilities = useCallback(async (productId: number) => {
    try {
      const compatibilities = await fetchProductCompatibilities(productId);
      setProductCompatibilities(prev => ({
        ...prev,
        [productId]: compatibilities
      }));
    } catch (error) {
      // Silently fail if compatibilities can't be loaded
      setProductCompatibilities(prev => ({
        ...prev,
        [productId]: []
      }));
    }
  }, []);

  // Load compatibilities for products when they're loaded
  useEffect(() => {
    products.forEach(product => {
      if (productCompatibilities[product.id] === undefined) {
        loadProductCompatibilities(product.id);
      }
    });
  }, [products, loadProductCompatibilities]);


  // Kategori seviyesini hesapla (hiyerarşi derinliği)
  const getCategoryLevel = (categoryId: number, categories: Category[]): number => {
    const category = categories.find(c => c.id === categoryId);
    if (!category || !category.parentId) return 0;
    return 1 + getCategoryLevel(category.parentId, categories);
  };

  // Kategorileri hiyerarşik sırala (parent-child ilişkisine göre)
  const getSortedCategoriesHierarchically = (categories: Category[]): Array<Category & { level: number; fullName: string }> => {
    const result: Array<Category & { level: number; fullName: string }> = [];
    
    // Recursive olarak kategori ve alt kategorilerini ekle
    const addCategoryAndChildren = (parentId: number | null | undefined, level: number = 0) => {
      const children = categories
        .filter(c => c.parentId === parentId)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
      
      children.forEach(cat => {
        // Kategori yolunu oluştur (parent > child formatında)
        let fullName = cat.categoryName;
        let currentCat = cat;
        while (currentCat.parentId) {
          const parent = categories.find(c => c.id === currentCat.parentId);
          if (!parent) break;
          fullName = `${parent.categoryName} > ${fullName}`;
          currentCat = parent;
        }
        
        result.push({
          ...cat,
          level,
          fullName
        });
        
        // Alt kategorileri recursive ekle
        addCategoryAndChildren(cat.id, level + 1);
      });
    };
    
    // Kök kategorilerden başla (parentId null/undefined olanlar)
    addCategoryAndChildren(null, 0);
    addCategoryAndChildren(undefined, 0);
    
    return result;
  };

  const categoriesWithHierarchy = getSortedCategoriesHierarchically(categories);

  const columns = [
    {
      title: expandableProducts.length > 0 ? (
        <Button 
          type="text"
          size="small"
          icon={isAllExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          onClick={isAllExpanded ? handleCollapseAll : handleExpandAll}
          style={{ padding: 0, height: 'auto' }}
        >
          {isAllExpanded ? 'Kapat' : 'Aç'}
        </Button>
      ) : '',
      key: 'expand',
      width: 50,
      align: 'center' as const,
      render: () => null, // Satırlarda boş
    },
    {
      title: 'Ürün Kodu · Marka',
      key: 'productCodeBrand',
      render: (_: any, record: Product) => (
        <div>
          <div>{record.productCode}</div>
          {record.productBrandName ? (
            <span style={{ fontSize: '12px', color: '#666' }}>{record.productBrandName}</span>
          ) : null}
        </div>
      )},
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName'},
    {
      title: '',
      key: 'image',
      width: 50,
      align: 'center' as const,
      render: (_: any, record: Product) => (
        productsWithImages[record.id] ? (
          <Button
            type="text"
            icon={<PictureOutlined style={{ color: '#1890ff', fontSize: '18px' }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleImageIconClick(record);
            }}
            style={{ padding: 0, height: 'auto' }}
            title="Görselleri göster"
          />
        ) : null
      )},
    {
      title: 'Toplam Stok',
      dataIndex: 'totalStock',
      key: 'totalStock',
      render: (_: any, record: Product) => record.totalStock ?? '-'},
    {
      title: 'Barkod',
      dataIndex: 'barcode',
      key: 'barcode'},
    {
      title: 'Kategori',
      dataIndex: 'categoryName',
      key: 'categoryName'},
    {
      title: 'Araç Uyumluluğu',
      key: 'vehicleCompatibility',
      width: 150,
      render: (_: any, record: Product) => {
        const compatibilities = productCompatibilities[record.id] || [];
        if (compatibilities.length === 0) return '-';
        
        const primary = compatibilities.find(c => c.isPrimary);
        const displayCompatibility = primary || compatibilities[0];
        
        return (
          <div>
            <Tag icon={<CarOutlined />} color="blue">
              {displayCompatibility.displayName}
            </Tag>
            {compatibilities.length > 1 && (
              <Tag>+{compatibilities.length - 1}</Tag>
            )}
          </div>
        );
      }},
    {
      title: 'Birim',
      dataIndex: 'baseUnitName',
      key: 'baseUnitName'},
    ...(canSeePurchasePrice
      ? [
          {
            title: 'Alış Fiyatı (KDV dahil)',
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            width: 120,
            render: (price: number, record: Product) =>
              price != null
                ? `${baseCurrencySymbol}${(price * (1 + (record.taxRate ?? 20) / 100)).toFixed(2)}`
                : '-'},
        ]
      : []),
    {
      title: 'Perakende Fiyatı (KDV dahil)',
      dataIndex: 'retailPrice',
      key: 'retailPrice',
      width: 140,
      render: (price: number, record: Product) =>
        price != null
          ? `${baseCurrencySymbol}${(price * (1 + (record.taxRate ?? 20) / 100)).toFixed(2)}`
          : '-'},
    {
      title: 'Toptan Fiyatı (KDV dahil)',
      dataIndex: 'wholesalePrice',
      key: 'wholesalePrice',
      width: 120,
      render: (price: number, record: Product) =>
        price != null
          ? `${baseCurrencySymbol}${(price * (1 + (record.taxRate ?? 20) / 100)).toFixed(2)}`
          : '-'},
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Aktif' : 'Pasif'}
        </span>
      )},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Product) => (
        <Space>
          <Tooltip title="Hareketler">
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => openMovementsPreview(record.id)}
            />
          </Tooltip>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDeleteProduct && (
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => {
                if ((record.totalStock ?? 0) > 0) {
                  message.warning('Stoğu olan ürün silinemez. Önce stoğu sıfırlamanız veya stok hareketi ile düşürmeniz gerekir.');
                  return;
                }
                Modal.confirm({
                  title: 'Bu ürünü silmek istediğinizden emin misiniz?',
                  okText: 'Evet',
                  cancelText: 'Hayır',
                  onOk: () => handleDelete(record)});
              }}
            >
              Sil
            </Button>
          )}
        </Space>
      )},
  ];

  const displayedColumns = useMemo(
    () => columns.filter((c) => visibleColumnKeys.includes(c.key as string)),
    [columns, visibleColumnKeys]
  );

  const toggleableColumnKeys = useMemo(
    () => columns.map((c) => c.key as string).filter((k) => !ALWAYS_VISIBLE_KEYS.includes(k)),
    [columns]
  );

  const columnDropdownContent = (
    <div
      style={{
        background: '#ffffff',
        padding: 12,
        borderRadius: 8,
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        minWidth: 240}}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Gösterilecek kolonlar</div>
      <Checkbox.Group
        value={visibleColumnKeys.filter((k) => !ALWAYS_VISIBLE_KEYS.includes(k))}
        onChange={(checked) => {
          const next = checked as string[];
          if (next.length === 0) {
            message.warning('En az bir kolon seçili olmalıdır.');
            return;
          }
          setVisibleColumnKeys([...ALWAYS_VISIBLE_KEYS, ...next]);
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        {toggleableColumnKeys.map((key) => (
          <Checkbox key={key} value={key}>
            {COLUMN_LABELS[key] ?? key}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h1 style={{ margin: 0 }}>Ürünler</h1>
          <Dropdown dropdownRender={() => columnDropdownContent} trigger={['click']} placement="bottomLeft">
            <Button icon={<UnorderedListOutlined />}>Kolonlar</Button>
          </Dropdown>
          <Tooltip title="Mevcut filtreye göre tüm ürünleri Excel'e aktar">
            <Button
              icon={<FileExcelOutlined />}
              loading={exportingExcel}
              onClick={handleExportExcel}
            >
              Excel'e Aktar
            </Button>
          </Tooltip>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Ürün
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Tooltip title="Ürün kodu veya adına göre arama">
            <Input
              placeholder="Ürün ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 300 }}
            />
          </Tooltip>
          <Tooltip title="Kategori: seçilen kategori ve alt kategorilerdeki ürünler listelenir">
            <div style={{ width: 200, display: 'inline-block', verticalAlign: 'top' }}>
              <Select
                placeholder="Kategori seçiniz"
                value={selectedCategory}
                onChange={(value) => {
                  setSelectedCategory(value);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const categoryName = categoriesWithHierarchy.find(c => c.id === option?.value)?.categoryName || '';
                  return categoryName.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {categoriesWithHierarchy.map(category => (
                  <Select.Option key={category.id} value={category.id}>
                    <span style={{ paddingLeft: `${category.level * 20}px` }}>
                      {category.level > 0 && '└─ '}
                      {category.categoryName}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Tooltip>
          <Tooltip title="Ürün markasına göre filtreleme">
            <div style={{ width: 180, display: 'inline-block', verticalAlign: 'top' }}>
              <Select
                placeholder="Ürün markası seçiniz"
                value={selectedProductBrand}
                onChange={(value) => {
                  setSelectedProductBrand(value);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const name = productBrands.find(b => b.id === option?.value)?.name || '';
                  return name.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {productBrands
                  .filter(b => b.isActive)
                  .sort((a, b) => {
                    if (a.isFavourite !== b.isFavourite) return b.isFavourite ? 1 : -1;
                    return a.name.localeCompare(b.name);
                  })
                  .map(pb => (
                    <Select.Option key={pb.id} value={pb.id}>
                      {pb.isFavourite && '⭐ '}
                      {pb.name}
                    </Select.Option>
                  ))}
              </Select>
            </div>
          </Tooltip>
          {categoryAttributeFilters.map(attr => {
            const v = attributeFilterValues[attr.id] ?? {};
            const label = attr.unit ? `${attr.name} (${attr.unit})` : attr.name;
            if (attr.dataType === AttributeDataType.Number) {
              return (
                <Space key={attr.id} size={4}>
                  <Tooltip title={`${label} — minimum değer (dahil)`}>
                    <InputNumber
                      placeholder={`${label} min`}
                      value={v.min}
                      onChange={min => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], min: min ?? undefined } }))}
                      style={{ width: 120 }}
                    />
                  </Tooltip>
                  <Tooltip title={`${label} — maksimum değer (dahil)`}>
                    <InputNumber
                      placeholder={`${label} max`}
                      value={v.max}
                      onChange={max => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], max: max ?? undefined } }))}
                      style={{ width: 120 }}
                    />
                  </Tooltip>
                </Space>
              );
            }
            if (attr.dataType === AttributeDataType.Text) {
              return (
                <Tooltip key={attr.id} title={`${label} — metin içerir araması`}>
                  <Input
                    placeholder={label}
                    value={v.value ?? ''}
                    onChange={e => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], value: e.target.value } }))}
                    style={{ width: 120 }}
                  />
                </Tooltip>
              );
            }
            if (attr.dataType === AttributeDataType.Date) {
              return (
                <Space key={attr.id} size={4}>
                  <Tooltip title={`${label} — başlangıç tarihi (dahil)`}>
                    <Input
                      type="date"
                      placeholder={`${label} başlangıç`}
                      value={v.dateMin ?? ''}
                      onChange={e => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], dateMin: e.target.value || undefined } }))}
                      style={{ width: 130 }}
                    />
                  </Tooltip>
                  <Tooltip title={`${label} — bitiş tarihi (dahil)`}>
                    <Input
                      type="date"
                      placeholder={`${label} bitiş`}
                      value={v.dateMax ?? ''}
                      onChange={e => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], dateMax: e.target.value || undefined } }))}
                      style={{ width: 130 }}
                    />
                  </Tooltip>
                </Space>
              );
            }
            return null;
          })}
          <Tooltip title="Filtreleri uygula ve listeyi yenile">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Ara
            </Button>
          </Tooltip>
        </Space>

        <Space style={{ marginBottom: 12, marginLeft: 12 }} size={16}>
          <Tooltip title="Araç markası, model ve model yılına göre ürün uyumluluğu filtresi">
            <Button
              icon={<CarOutlined />}
              onClick={() => setCompatibilityDrawerOpen(true)}
            >
              Araç uyumluluk
              {(selectedBrand || selectedModel != null || yearFrom != null || yearTo != null) && (
                <span style={{ marginLeft: 4, opacity: 0.85 }}>(filtre aktif)</span>
              )}
            </Button>
          </Tooltip>
          <Space size={6}>
            <span style={{ color: '#595959' }}>Stokta olanlar</span>
            <Tooltip title="Açıkken yalnızca toplam stoku sıfırdan büyük ürünler listelenir">
              <Switch
                checked={inStockOnly}
                onChange={(checked) => {
                  setInStockOnly(checked);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
              />
            </Tooltip>
          </Space>
        </Space>

        <Drawer
          title={<><CarOutlined /> Araç uyumluluk</>}
          placement="right"
          open={compatibilityDrawerOpen}
          onClose={() => setCompatibilityDrawerOpen(false)}
          width={360}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Tooltip title="Araç markası — ürün uyumluluğu filtresi">
              <div style={{ width: '100%' }}>
                <Select
                  placeholder="Marka seçiniz"
                  value={selectedBrand}
                  onChange={(value) => {
                    setSelectedBrand(value);
                    setSelectedModel(undefined);
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const brandName = brands.find(b => b.id === option?.value)?.name || '';
                    return brandName.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {brands
                    .filter(b => b.isActive)
                    .sort((a, b) => {
                      if (a.isFavourite !== b.isFavourite) return b.isFavourite ? 1 : -1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(brand => (
                      <Select.Option key={brand.id} value={brand.id}>
                        {brand.isFavourite && '⭐ '}
                        {brand.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            </Tooltip>
            <Tooltip title="Araç modeli — ürün uyumluluğu filtresi">
              <div style={{ width: '100%' }}>
                <Select
                  placeholder="Model seçiniz"
                  value={selectedModel}
                  onChange={(value) => {
                    setSelectedModel(value);
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  disabled={!selectedBrand}
                  filterOption={(input, option) => {
                    const modelName = models.find(m => m.id === option?.value)?.name || '';
                    return modelName.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {models
                    .filter(m => m.isActive)
                    .sort((a, b) => {
                      if (a.isFavourite !== b.isFavourite) return b.isFavourite ? 1 : -1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(model => (
                      <Select.Option key={model.id} value={model.id}>
                        {model.isFavourite && '⭐ '}
                        {model.displayName}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            </Tooltip>
            <Tooltip title="Uyumluluk başlangıç yılı">
              <div style={{ width: '100%' }}>
                <InputNumber
                  placeholder="Yıl (Başlangıç)"
                  value={yearFrom}
                  onChange={(value) => {
                    setYearFrom(value ? value : undefined);
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                  style={{ width: '100%' }}
                  min={1900}
                  max={2100}
                  integerOnly
                />
              </div>
            </Tooltip>
            <Tooltip title="Uyumluluk bitiş yılı">
              <div style={{ width: '100%' }}>
                <InputNumber
                  placeholder="Yıl (Bitiş)"
                  value={yearTo}
                  onChange={(value) => {
                    setYearTo(value ? value : undefined);
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                  style={{ width: '100%' }}
                  min={1900}
                  max={2100}
                  integerOnly
                />
              </div>
            </Tooltip>
            <Button type="primary" block onClick={() => setCompatibilityDrawerOpen(false)}>
              Tamam
            </Button>
          </Space>
        </Drawer>
      </div>

      <div style={{ width: '100%', overflowX: 'auto' }}>
        <Table
          scroll={{ x: 'max-content' }}
          columns={displayedColumns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          expandable={{
          expandedRowKeys,
          rowExpandable: (record) => {
            // isPriceVariant false ise varyant gösterme
            if (!record.isPriceVariant) {
              return false;
            }
            // Varyantı olan ürünlerde + işareti göster
            const variants = variantsByProduct[record.id];
            return variants ? variants.length > 0 : true; // İlk tıklamada kontrol et
          },
          onExpand: async (expanded, record) => {
            // isPriceVariant false ise varyant işleme
            if (!record.isPriceVariant) {
              return;
            }
            
            if (expanded) {
              if (!variantsByProduct[record.id]) {
                const rows = await fetchProductVariants(record.id);
                setVariantsByProduct(prev => ({ ...prev, [record.id]: rows }));
                // Varyant yoksa expand'i kapat
                if (rows.length === 0) {
                  setExpandedRowKeys(keys => keys.filter(k => k !== record.id));
                  message.info('Bu ürünün varyantı bulunmamaktadır.');
                  return;
                }
              }
              setExpandedRowKeys(keys => [...keys, record.id]);
            } else {
              setExpandedRowKeys(keys => keys.filter(k => k !== record.id));
            }
          },
          expandedRowRender: (record: Product) => {
            const variants = variantsByProduct[record.id] || [];
            const taxRate = record.taxRate ?? 20;
            const withVat = (p: number) => p * (1 + taxRate / 100);
            if (variants.length === 0) {
              return <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>Bu ürünün varyantı bulunmamaktadır.</div>;
            }
            return (
              <Table
                size="small"
                rowKey="id"
                columns={[
                  { title: 'SKU', dataIndex: 'sku', width: 150 },
                  ...(canSeePurchasePrice
                    ? [
                        {
                          title: 'Alış Fiyatı (KDV dahil)',
                          dataIndex: 'purchasePrice',
                          width: 120,
                          render: (price: number) => price != null ? `${baseCurrencySymbol}${withVat(price).toFixed(2)}` : '-'},
                      ]
                    : []),
                  {
                    title: 'Perakende Fiyatı (KDV dahil)',
                    dataIndex: 'price',
                    width: 140,
                    render: (val: number) => val != null ? `${baseCurrencySymbol}${withVat(val).toFixed(2)}` : '-'},
                  {
                    title: 'Toptan Fiyatı (KDV dahil)',
                    dataIndex: 'wholesalePrice',
                    width: 120,
                    render: (price: number) => price != null ? `${baseCurrencySymbol}${withVat(price).toFixed(2)}` : '-'},
                  { title: 'Stok', dataIndex: 'stock', width: 100 },
                  { title: 'Değerler', render: (_: any, v: ProductVariantDto) => v.values.map(x => `${x.variantName}: ${x.variantValue}`).join(' | ') }
                ]}
                dataSource={variants}
                pagination={false}
              />
            );
          }
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Toplam ${total} kayıt`,
          pageSizeOptions: ['10', '20', '50', '100']}}
        onChange={handleTableChange}
        />
      </div>

      <Modal
        title="Ürün Görselleri"
        open={imageGalleryVisible}
        onCancel={() => setImageGalleryVisible(false)}
        footer={null}
        width={800}
        centered
      >
        <Image.PreviewGroup>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px', marginTop: '16px' }}>
            {selectedProductImages.map((image) => (
              <div key={image.id} style={{ position: 'relative' }}>
                <Image
                  src={`${FILE_BASE_URL}${image.thumbnailFilePath}`}
                  alt={image.originalFileName}
                  style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                  preview={{
                    src: `${FILE_BASE_URL}${image.originalFilePath}`
                  }}
                />
                {image.isPrimary && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(250, 173, 20, 0.9)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    Ana
                  </div>
                )}
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      </Modal>

      <Modal
        title={
          movementsPreview
            ? `Hareket özeti — ${movementsPreview.productCode} · ${movementsPreview.productName}`
            : 'Hareket özeti'
        }
        open={movementsModalOpen}
        onCancel={closeMovementsModal}
        width={920}
        destroyOnClose
        footer={[
          <Button key="close" onClick={closeMovementsModal}>
            Kapat
          </Button>,
          <Button key="all" type="primary" onClick={goToFullMovementsPage} disabled={movementsTargetProductId == null}>
            Tümünü göster
          </Button>,
        ]}
      >
        <Spin spinning={movementsPreviewLoading}>
          {movementsPreview && (
            <>
              <Space wrap style={{ marginBottom: 16, width: '100%' }}>
                <Card size="small" title="Son Alış Fiyatı">
                  {formatMovementMoney(movementsPreview.summary.lastPurchasePrice)}
                </Card>
                <Card size="small" title="Son Satış Fiyatı">
                  {formatMovementMoney(movementsPreview.summary.lastSalePrice)}
                </Card>
                <Card size="small" title="Sistemdeki Satış Fiyatı">
                  {formatMovementMoney(movementsPreview.summary.systemSalePrice)}
                </Card>
                <Card size="small" title="Ortalama Fiyat (Paçal)">
                  {formatMovementMoney(movementsPreview.summary.averagePurchasePrice)}
                </Card>
              </Space>
              <Table<ProductMovementPreviewItem>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={movementsPreview.items}
                columns={[
                  {
                    title: 'Tarih',
                    dataIndex: 'movementDate',
                    key: 'movementDate',
                    width: 132,
                    render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm')},
                  {
                    title: 'Tür',
                    dataIndex: 'movementLabel',
                    key: 'movementLabel',
                    width: 88,
                    render: (v: string) => (
                      <Tag color={v === 'Alış' ? 'green' : v === 'Satış' ? 'blue' : 'default'}>{v}</Tag>
                    )},
                  {
                    title: 'Belge / Servis',
                    key: 'source',
                    ellipsis: true,
                    render: (_: unknown, r: ProductMovementPreviewItem) => {
                      if (r.serviceOrderNo)
                        return `${r.serviceOrderNo}${r.serviceCustomerName ? ` - ${r.serviceCustomerName}` : ''}`;
                      return r.documentNo || '-';
                    }},
                  {
                    title: 'Miktar',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    width: 72,
                    align: 'right'},
                  {
                    title: 'Birim Fiyat',
                    dataIndex: 'unitPrice',
                    key: 'unitPrice',
                    width: 108,
                    align: 'right',
                    render: (v: number | null) => formatMovementMoney(v)},
                  {
                    title: 'Toplam',
                    dataIndex: 'totalPrice',
                    key: 'totalPrice',
                    width: 108,
                    align: 'right',
                    render: (v: number | null) => formatMovementMoney(v)},
                ]}
              />
              <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                Listelenen son 5 hareket. Tüm hareketler için aşağıdaki &quot;Tümünü göster&quot; düğmesini kullanın.
              </div>
            </>
          )}
        </Spin>
      </Modal>

    </div>
  );
};

export default Products;
